'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@/shared/lib/cn';
import { SwipeToRevealRow } from '@/shared/ui/SwipeToRevealRow';
import { formatEstimateAmount } from '@/shared/lib/utils';
import { EstimateUnitSelect } from './EstimateUnitSelect';
import type {
  EstimateItem,
  UnitOption,
  UpdateEstimateItemRequest,
} from '../api/estimate-service';

/**
 * Parse a user-typed decimal field, tolerating `,` as the decimal separator;
 * `null` for empty, garbage, negative or non-finite input (Apple/Android
 * `parseEstimateDecimal` parity — the API validates `min:0`). The explicit
 * empty check matters: `Number('')` is `0`, so without it clearing a field
 * would PATCH a real `0` to the server.
 */
function parseEstimateDecimal(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed.replace(',', '.'));
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

interface EstimateItemRowProps {
  item: EstimateItem;
  /** Server-driven unit picker options (current measurement system). */
  unitOptions: UnitOption[];
  disabled?: boolean;
  /** Schedule a debounced PATCH for one field of this item. */
  onFieldChange: (itemId: string, data: UpdateEstimateItemRequest) => void;
  /** Flush any pending debounced edit for this item (blur/Enter). */
  onFieldCommit: (itemId: string) => void;
  onDelete: (itemId: string) => void;
  /** Swipe-to-reveal open state — hoisted so only one row stays open at a time. */
  revealed: boolean;
  onRevealedChange: (open: boolean) => void;
  /** This row was just added (add button / delete-undo restore): scroll it
   *  into view and run the flash animation once, then report back via
   *  onJustAddedShown so later renders don't re-trigger it. */
  justAdded?: boolean;
  onJustAddedShown?: () => void;
}

/**
 * One inline-editable estimate line (compact, Apple parity): a borderless name
 * field, then a quantity·unit pair / unit price / line total — no field labels,
 * numeric inputs right-aligned so digits sit against the unit and prices read
 * as a column of figures. `line_total` is server-computed and read-only.
 *
 * Two responsive arrangements, switched by CONTAINER width (`@min-[30rem]` (480px) on the
 * `@container` items list in EstimateSection — the editor column narrows in
 * two-column mode, so viewport breakpoints would lie):
 * - narrow (phones): two lines — name over the figures cluster;
 * - `@min-[30rem]` (480px)+: ONE line — name takes the available space, then the
 *   figures cluster with compacted columns (Apple's 56/72/76pt parity); the two
 *   inner row divs flatten via `display: contents`.
 *
 * Local draft state mirrors the cached value but lets the user type freely;
 * each keystroke schedules a debounced PATCH and blur flushes it immediately.
 *
 * No delete glyph and no drag handle (Apple/Android parity): the whole row is
 * the reorder lift (dnd-kit delay activation — long-press to pick it up) and
 * delete is a swipe-to-reveal trailing action (`SwipeToRevealRow`). The per-row
 * trash icon and grip handle were removed deliberately — don't reintroduce them.
 */
export function EstimateItemRow({
  item,
  unitOptions,
  disabled = false,
  onFieldChange,
  onFieldCommit,
  onDelete,
  revealed,
  onRevealedChange,
  justAdded = false,
  onJustAddedShown,
}: EstimateItemRowProps) {
  const t = useTranslations('common.estimate.editor');

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled });

  // Just-added row: scroll into view once on mount/flag — the flash itself is
  // the CSS animation class below (teal wash holds briefly, then fades).
  const rowRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!justAdded) return;
    rowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [justAdded]);

  // Draft buffers — re-sync when the server value changes (e.g. WS reconcile)
  // and the field isn't being typed into right now.
  const [name, setName] = useState(item.name);
  const [quantity, setQuantity] = useState(String(item.quantity));
  const [unitPrice, setUnitPrice] = useState(String(item.unit_price));

  useEffect(() => setName(item.name), [item.name]);
  useEffect(() => setQuantity(String(item.quantity)), [item.quantity]);
  useEffect(() => setUnitPrice(String(item.unit_price)), [item.unit_price]);

  // Unparseable numeric drafts (garbage, emptied field) flip the focus outline
  // and text to the destructive red while typing; blur reverts the draft to
  // the committed value, so the state never survives the edit session
  // (cross-platform estimate-editor pattern).
  const quantityInvalid = parseEstimateDecimal(quantity) === null;
  const unitPriceInvalid = parseEstimateDecimal(unitPrice) === null;

  const commitName = (value: string) => {
    const trimmed = value.trim();
    if (trimmed && trimmed !== item.name) {
      onFieldChange(item.id, { name: trimmed });
    }
  };

  // A unit pick is discrete — PATCH immediately (no debounce window). The
  // currently displayed quantity and unit price ride along so the visible
  // numbers stay fixed and only their denomination changes (the server
  // interprets both under the NEW unit).
  const commitUnit = (next: string | null) => {
    onFieldChange(item.id, {
      unit: next,
      quantity: parseEstimateDecimal(quantity) ?? item.quantity,
      unit_price: parseEstimateDecimal(unitPrice) ?? item.unit_price,
    });
    onFieldCommit(item.id);
  };

  const commitNumber = (
    raw: string,
    field: 'quantity' | 'unit_price',
    current: number
  ) => {
    const parsed = parseEstimateDecimal(raw);
    if (parsed === null || parsed === current) return;
    onFieldChange(item.id, { [field]: parsed });
  };

  const blurOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
  };

  // Name lives in an auto-growing textarea (long names wrap instead of
  // truncating); Enter commits — a line break is never part of a name.
  const blurOnEnterTextarea = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      (e.target as HTMLTextAreaElement).blur();
    }
  };

  return (
    <div
      ref={(node) => {
        setNodeRef(node);
        rowRef.current = node;
      }}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      {...attributes}
      {...listeners}
      onAnimationEnd={justAdded ? onJustAddedShown : undefined}
      className={cn(
        'outline-none',
        isDragging && 'relative z-10 opacity-90 shadow-md',
        justAdded && 'animate-estimate-row-flash'
      )}
    >
      <SwipeToRevealRow
        onDelete={() => onDelete(item.id)}
        revealed={revealed}
        onRevealedChange={onRevealedChange}
        deleteLabel={t('deleteItem')}
        disabled={disabled}
      >
        <div className="space-y-2 px-4 py-3 @min-[30rem]:flex @min-[30rem]:items-center @min-[30rem]:gap-2 @min-[30rem]:space-y-0">
          {/* Row 1: name. The focused field gets brand-teal text plus an active
              teal outline drawn OUTSIDE its bounds (outline-offset — no layout
              shift; cross-platform estimate-editor pattern). At @min-[30rem] the
              wrapper dissolves (display: contents) into the one-line flex row. */}
          <div className="flex items-center gap-2 @min-[30rem]:contents">
            <textarea
              value={name}
              rows={1}
              onChange={(e) => {
                const next = e.target.value.replace(/\n/g, ' ');
                setName(next);
                commitName(next);
              }}
              onBlur={() => {
                // A blanked-out name is never committed — revert the draft.
                if (!name.trim()) setName(item.name);
                onFieldCommit(item.id);
              }}
              onKeyDown={blurOnEnterTextarea}
              disabled={disabled}
              placeholder={t('itemNamePlaceholder')}
              aria-label={t('columns.name')}
              className="min-w-0 flex-1 resize-none rounded-md bg-transparent text-card-meta field-sizing-content text-foreground/70 outline-none placeholder:text-muted-foreground focus:text-primary focus:outline-[1.5px] focus:outline-offset-2 focus:outline-primary focus:outline-solid disabled:opacity-50"
            />
          </div>

          {/* Row 2: ······ quantity·unit · unit price · line total.
              Names anchor left (row above); the figures cluster right next to
              the line total so the layout reads as a name column on the left
              and a number column on the right. At @min-[30rem] this wrapper
              dissolves too (display: contents) — the figures follow the name on
              the same line with compacted column widths. The text-* classes
              still inherit through display: contents. */}
          <div className="flex items-center gap-2 text-card-meta text-muted-foreground @min-[30rem]:contents">
            {/* Quantity + unit read as ONE figure column ("243.03 m²", "4 pcs"):
                the quantity is right-aligned and the unit sits immediately after
                it with a hair of gap, so the pair scans as a single value rather
                than two scattered columns. The old "×" separator is gone — three
                visual columns remain: quantity·unit / unit price / line total. */}
            <div className="ms-auto flex items-center gap-1 @min-[30rem]:ms-0">
              <input
                value={quantity}
                inputMode="decimal"
                onChange={(e) => {
                  setQuantity(e.target.value);
                  commitNumber(e.target.value, 'quantity', item.quantity);
                }}
                onBlur={() => {
                  // An invalid draft (garbage/empty) must not survive the edit
                  // session — revert to the committed value (only parsed
                  // keystrokes ever reached the cache).
                  setQuantity(String(item.quantity));
                  onFieldCommit(item.id);
                }}
                onKeyDown={blurOnEnter}
                disabled={disabled}
                aria-label={t('columns.quantity')}
                placeholder={t('columns.quantity')}
                className={cn(
                  'w-14 rounded-md bg-transparent text-end font-mono text-foreground/70 outline-none tabular-nums placeholder:text-muted-foreground focus:outline-[1.5px] focus:outline-offset-2 focus:outline-solid disabled:opacity-50 @min-[30rem]:w-13',
                  quantityInvalid
                    ? 'focus:text-destructive focus:outline-destructive'
                    : 'focus:text-primary focus:outline-primary'
                )}
              />
              <EstimateUnitSelect
                value={item.unit}
                options={unitOptions}
                disabled={disabled}
                onChange={commitUnit}
              />
            </div>
            <input
              value={unitPrice}
              inputMode="decimal"
              onChange={(e) => {
                setUnitPrice(e.target.value);
                commitNumber(e.target.value, 'unit_price', item.unit_price);
              }}
              onBlur={() => {
                // An invalid draft (garbage/empty) must not survive the edit
                // session — revert to the committed value (only parsed
                // keystrokes ever reached the cache).
                setUnitPrice(String(item.unit_price));
                onFieldCommit(item.id);
              }}
              onKeyDown={blurOnEnter}
              disabled={disabled}
              aria-label={t('columns.unitPrice')}
              placeholder={t('columns.unitPrice')}
              className={cn(
                'w-12 rounded-md bg-transparent text-end font-mono text-foreground/70 outline-none tabular-nums placeholder:text-muted-foreground focus:outline-[1.5px] focus:outline-offset-2 focus:outline-solid disabled:opacity-50 @min-[30rem]:w-12',
                unitPriceInvalid
                  ? 'focus:text-destructive focus:outline-destructive'
                  : 'focus:text-primary focus:outline-primary'
              )}
            />

            {/* Plain amount, no currency — the currency renders only in the
                totals summary (cross-platform convention). Inherits the
                wrapper's text-card-meta size (through display: contents at
                @min-[30rem]) so all digits in the row read at one size. Must
                never wrap when the row gets tight — the flexible inputs compress
                instead. The uniform min-width keeps the total column equal
                across rows so the line totals stack on one vertical line. */}
            <span className="inline-block min-w-14 whitespace-nowrap text-end font-mono text-foreground/70 tabular-nums @min-[30rem]:min-w-14">
              {formatEstimateAmount(item.line_total)}
            </span>
          </div>
        </div>
      </SwipeToRevealRow>
    </div>
  );
}
